import {
  ConfirmationResult,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signOut,
} from "firebase/auth";
import { authService } from "../../firebase-config";
import { platformTool } from "../../utils/PlatformTool";
import { soulApi } from "../api/SoulApi";
import { SoulLoginParams } from "../models/Soul/SoulLoginParams";

class AuthManager {
  private static instance: AuthManager;

  public static get(): AuthManager {
    if (!this.instance) {
      this.instance = new AuthManager();
    }
    return this.instance;
  }

  async login(email: string, password: string): Promise<boolean> {
    try {
      const userCredential = await signInWithEmailAndPassword(
        authService,
        email,
        password
      );
      const user = userCredential.user;
      const deviceData = await platformTool.getDeviceData();
      const data: SoulLoginParams = {
        userId: user.uid,
        deviceData,
      };
      return this.sendLoginDataToBackend(data);
    } catch (error) {
      console.error("Error en login:", error);
      return false;
    }
  }

  getAuthToken(): string | undefined {
    return authService.currentUser?.uid;
  }

  isLoggedIn(): boolean {
    return !!authService.currentUser;
  }

  async logout(): Promise<void> {
    try {
      await signOut(authService);
    } catch (error) {
      console.error("Error en logout:", error);
    }
  }

  private async sendLoginDataToBackend(
    params: SoulLoginParams
  ): Promise<boolean> {
    const result = await soulApi.soulLogin.request(params);
    if (result.isSuccess) {
      return true;
    }
    return false;
  }

  async sendSmsCode(
    phoneNumber: string,
    recaptchaVerifier: any
  ): Promise<ConfirmationResult | undefined> {
    const confirmationResult = await signInWithPhoneNumber(
      authService,
      phoneNumber,
      recaptchaVerifier
    );
    return confirmationResult;
  }

  async sendWhatsappCode(phoneNumber: string): Promise<boolean | undefined> {
    const result = await soulApi.soulAuth.request({ phoneNumber });
    if (!result?.isSuccess) {
      throw new Error("Error on send whatsapp code");
    }
    return true;
  }

  async sendEmailCode(
    email: string,
    passport: string
  ): Promise<boolean | undefined> {
    const result = await soulApi.soulAuth.request({ email, passport });
    if (!result?.isSuccess) {
      throw new Error(result.messageError ?? "Error on send email code");
    }
    return true;
  }

  async loginWithPhone(
    confirmationResult: ConfirmationResult | undefined,
    code: string
  ): Promise<boolean> {
    const userCredential = await confirmationResult!.confirm(code);
    const deviceData = await platformTool.getDeviceData();
    const data: SoulLoginParams = {
      userId: userCredential.user.uid,
      deviceData,
    };
    await this.sendLoginDataToBackend(data);
    return true;
  }

  async loginWithWhatsapp({
    phoneNumber,
    code,
  }: {
    phoneNumber: string;
    code: string;
  }): Promise<boolean> {
    const result = await soulApi.soulCheckAuth.request({
      phoneNumber,
      code,
    });
    if (!result?.isSuccess) {
      throw new Error("Error on check whatsapp code");
    }
    const deviceData = await platformTool.getDeviceData();
    const data: SoulLoginParams = {
      userId: result.data?.uid!,
      deviceData,
    };
    await this.sendLoginDataToBackend(data);
    await signInWithCustomToken(authService, result.data?.token!);
    return true;
  }

  async loginWithEmail({
    email,
    code,
    passport,
  }: {
    email: string;
    code: string;
    passport: string;
  }): Promise<boolean> {
    const result = await soulApi.soulCheckAuth.request({
      email,
      code,
    });
    if (!result?.isSuccess) {
      throw new Error(result.messageError ?? "Error on check email code");
    }
    const deviceData = await platformTool.getDeviceData();
    await signInWithCustomToken(authService, result.data?.token!);
    const token = await authService.currentUser!.getIdToken();
    const data: SoulLoginParams = {
      userId: result.data?.uid!,
      deviceData,
      email,
      passport,
      customToken: token,
    };
    await this.sendLoginDataToBackend(data);
    return true;
  }
}

export const authManager = AuthManager.get();
